import { useState, useRef, useEffect } from 'react';
import Image from 'next/legacy/image';
import { ProductComposedInstanceMedia, ProductInstanceMedia } from 'types';
import { NextIcon } from 'components/NextImage';
import { ItemImageNotes } from 'components/ItemImageNotes';
import CharityLogo from 'components/CharityLogo';

import cx from 'classnames';
import styles from './ProductPage.module.scss';
import { useSwipeable } from 'react-swipeable';

export type PageSliderProps = {
  composedMedia?: ProductComposedInstanceMedia[];
  deleteCallback?: (id: string) => void;
  isSold?: boolean;
  media?: ProductInstanceMedia;
  isCharity?: boolean;
};

const composeMediaList = (media: ProductInstanceMedia) => {
  const { images = [], video } = media;
  const mediaList = images.map((image) => ({ type: 'image', url: image } as ProductComposedInstanceMedia));
  video && mediaList.push({ type: 'video', url: video });

  return mediaList;
};

const checkIsDeletePossible = (
  activeMedia: ProductComposedInstanceMedia,
  composedMedia?: ProductComposedInstanceMedia[],
  deleteCallback?: (id: string) => void,
) => {
  if (!deleteCallback) return false;
  if (activeMedia?.type === 'video') {
    return !!composedMedia?.filter((media) => media.type === 'image').length;
  }
  return (composedMedia?.filter((media) => media.type === 'image').length || 0) > 1;
};

export const PageSlider = ({ media, composedMedia, deleteCallback, isSold, isCharity }: PageSliderProps) => {
  const [mediaList, setMediaList] = useState(composedMedia || composeMediaList(media || ({} as ProductInstanceMedia)));
  const [activeMediaIndex, setActiveMediaIndex] = useState(0);
  const [activeMedia, setActiveMedia] = useState<ProductComposedInstanceMedia>(mediaList[activeMediaIndex]);
  const [windowWith, setWindowWith] = useState<number | null>(null);
  const [isDeletePossible, setIsDeletePossible] = useState<boolean>(
    checkIsDeletePossible(mediaList[activeMediaIndex], composedMedia, deleteCallback),
  );
  const mainImageContainerRef = useRef<HTMLDivElement>(null);
  const sideNavContainerRef = useRef<HTMLDivElement>(null);
  const sideTabletNavContainerRef = useRef<HTMLDivElement>(null);
  const mediaCount = mediaList.length;

  useEffect(() => {
    setMediaList(composeMediaList(media || ({} as ProductInstanceMedia)))
  }, [media])

  useEffect(() => {
    window.addEventListener('resize', trackResize);

    return () => {
      window.removeEventListener('resize', trackResize);
    };
  }, []);

  useEffect(() => {
    if (mainImageContainerRef?.current?.clientWidth && sideNavContainerRef.current) {
      sideNavContainerRef.current.setAttribute(
        'style',
        `height:${String(mainImageContainerRef.current.clientWidth)}px;`,
      );
    }
    if (mainImageContainerRef?.current?.clientWidth && sideTabletNavContainerRef.current) {
      sideTabletNavContainerRef.current.setAttribute('style', `width: 336px;`);
    }
    setTimeout(() => {
      if (mainImageContainerRef?.current?.clientWidth && sideTabletNavContainerRef.current) {
        sideTabletNavContainerRef.current.setAttribute(
          'style',
          `width:${String(mainImageContainerRef.current.clientWidth)}px;`,
        );
      }
    }, 100);
  }, [windowWith]);

  useEffect(() => {
    setActiveMedia(mediaList[0]);
  }, [mediaList]);

  useEffect(() => {
    setIsDeletePossible(checkIsDeletePossible(activeMedia, mediaList, deleteCallback));
  }, [activeMedia]); // eslint-disable-line react-hooks/exhaustive-deps

  const trackResize = () => {
    setWindowWith(window.innerWidth);
  };

  const handleIndexChange = (index: number) => {
    setActiveMedia(mediaList[index]);
    setActiveMediaIndex(index);
  };

  const handleImageClick = (index: number) => {
    handleIndexChange(index);
  };

  const handleIconClick = (next: number) => {
    if (next > mediaCount - 1) {
      handleIndexChange(0);
    } else if (next < 0) {
      handleIndexChange(mediaCount - 1);
    } else {
      handleIndexChange(next);
    }
  };

  const onDelete = (id: string) => {
    setMediaList((previous) => previous.filter((media) => media.id !== id));
    deleteCallback && deleteCallback(id);
  };

  const renderNav = () => (
    <>
      {mediaList.map((media, index) => (
        <div key={index} className={styles.sideNavItemWrapper} onClick={() => handleImageClick(index)}>
          <div className={cx(styles.sideNavItem, {[styles.selected]: activeMediaIndex === index})}>
            {media.type === 'video' ? (
              <video loop autoPlay muted>
                <source src={media.url} />
              </video>
            ) : (
              <Image src={media.url} layout="fill" objectFit="contain" alt={`Item image ${index + 1}`} />
            )}
          </div>
        </div>
      ))}
    </>
  );

  const renderMobileNav = () => (
    <>
      {mediaList.map((media, index) => (
        <div
          key={index}
          className={cx(styles.sideMobileNavItem, { [styles.mobNavActive]: activeMediaIndex === index })}
          onClick={() => handleImageClick(index)}
        />
      ))}
    </>
  );

  const handlers = useSwipeable({
    onSwipedLeft: () => handleIconClick(activeMediaIndex + 1),
    onSwipedRight: () => handleIconClick(activeMediaIndex - 1),
    preventDefaultTouchmoveEvent: true,
    trackMouse: true
  });

  return (
    <div className={styles.slider}>
      <div className={styles.forDesktopOnly}>
        <div ref={sideNavContainerRef} className={styles.sideNav}>
          {renderNav()}
        </div>
      </div>
      <div ref={mainImageContainerRef} className={styles.mainItem} {...handlers}>
        {mediaCount > 1 && (
          <>
            <div className={styles.previousMedia} onClick={() => handleIconClick(activeMediaIndex - 1)}>
              <Image src="/icons/arrows/CaretLeftSoft.svg" width={32} height="32" alt="Previous" />
            </div>
            <div className={styles.nextMedia} onClick={() => handleIconClick(activeMediaIndex + 1)}>
              <Image src="/icons/arrows/CaretRightSoft.svg" width={32} height="32" alt="Next" />
            </div>
          </>
        )}
        {isDeletePossible && (
          <div className={styles.deleteMediaIcon} onClick={() => onDelete(activeMedia.id || '')}>
            <NextIcon src="/icons/office/TrashWarn.svg" size={40} />
          </div>
        )}
        {activeMedia && activeMedia.type === 'video' ? (
          <video controls>
            <source src={activeMedia.url} />
          </video>
        ) : (
          activeMedia && (
            <>
              <Image src={activeMedia.url} layout="fill" objectFit="contain" alt="Item image" />
              {isCharity && <CharityLogo />}
            </>
          )
        )}
        {isSold && <ItemImageNotes withCover={true} texting="This item has been sold." />}
      </div>
      <div className={styles.forNotDesktopOnly}>
        <div ref={sideTabletNavContainerRef} className={styles.sideNav}>
          {renderNav()}
        </div>
      </div>
      {/*<div className={styles.forMobileOnly}>*/}
      {/*  <div className={styles.mobileSideNav}>{renderMobileNav()}</div>*/}
      {/*</div>*/}
    </div>
  );
};
